import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const shouldJump = newMediaGallery.hasAttribute('data-unattached-first');
    const mediaId = newMediaGallery.getAttribute('data-variant-media-id');
    const currentMediaId = this.getAttribute('data-variant-media-id');

    const currentSlideshow = this.slideshow;
    const currentIndex = currentSlideshow?.current;
    const visibleSlides = currentSlideshow?.slides;
    const currentSlide = visibleSlides?.[currentIndex] || currentSlideshow?.refs?.slides?.[currentIndex];
    const visibleSlide = currentSlideshow?.querySelector?.('slideshow-slide[aria-hidden="false"]');
    const mockupSlide = this.querySelector('slideshow-slide[slide-id="live-mockup"]');
    const inSituSlide = this.querySelector('slideshow-slide[slide-id="in-situ"]');
    const inSitu2Slide = this.querySelector('slideshow-slide[slide-id="in-situ-2"]');
    const scroller = currentSlideshow?.refs?.scroller || this.querySelector('slideshow-container') || this;
    const visuallyOnSlide = (slide) => {
      if (!slide) return false;
      const slideBox = slide.getBoundingClientRect();
      const viewBox = scroller.getBoundingClientRect();
      const overlap = Math.min(slideBox.right, viewBox.right) - Math.max(slideBox.left, viewBox.left);
      return overlap > viewBox.width * 0.4;
    };
    const visuallyOnMockup =
      visuallyOnSlide(mockupSlide) || visuallyOnSlide(inSituSlide) || visuallyOnSlide(inSitu2Slide);

    // Keep the existing carousel so size/frame/mat updates don't remount on the
    // lifestyle photo. Swipe/arrows are the only way back to that first image.
    if (shouldJump && mediaId === 'live-mockup' && currentMediaId === 'live-mockup') {
      if (!visuallyOnMockup && mockupSlide) {
        currentSlideshow?.select?.({ id: 'live-mockup' }, undefined, { animate: true });
        requestAnimationFrame(() => {
          const viewBox = scroller.getBoundingClientRect();
          const slideBox = mockupSlide.getBoundingClientRect();
          const overlap = Math.min(slideBox.right, viewBox.right) - Math.max(slideBox.left, viewBox.left);
          if (overlap <= viewBox.width * 0.4) {
            mockupSlide.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
          }
        });
      }
      return;
    }

    const wasOnTarget =
      Boolean(mediaId) &&
      (currentSlide?.getAttribute('slide-id') === String(mediaId) ||
        visibleSlide?.getAttribute('slide-id') === String(mediaId));

    if (wasOnTarget && mediaId) {
      const nextSlides = [...newMediaGallery.querySelectorAll('slideshow-slide')];
      const targetIndex = nextSlides.findIndex((slide) => slide.getAttribute('slide-id') === String(mediaId));
      if (targetIndex >= 0) {
        newMediaGallery.querySelector('slideshow-component')?.setAttribute('initial-slide', String(targetIndex));
      }
    }

    const section = this.closest('.shopify-section, dialog');

    this.replaceWith(newMediaGallery);

    if (!shouldJump || !mediaId) return;

    const animate = !wasOnTarget;
    const targetId = String(mediaId);

    const getSlideshow = () => {
      const gallery = newMediaGallery.isConnected
        ? newMediaGallery
        : section?.querySelector?.('media-gallery') || document.querySelector('media-gallery');
      return gallery?.slideshow || gallery?.querySelector('slideshow-component');
    };

    const jumpToVariantMedia = (useAnimate) => {
      getSlideshow()?.select?.({ id: targetId }, undefined, { animate: useAnimate });
    };

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          jumpToVariantMedia(animate);
        });
      });
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const index = event.detail.slideIndex ?? event.detail.index;
    this.slideshow?.select(index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
